import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCalendarAlt, faUser, faCopy } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import SoftBadge from 'components/common/SoftBadge';

const resumenHistorialDefault = {
    iTotalMovimientos: 0,
    iTotalTimbresComprados: 0,
    iEventosCompraTimbres: 0,
    iEventosUsoTimbres: 0,
    iTotalTimbresUsados: 0,
    iTotalCambiosRfc: 0,
    dFechaUltimoCambioRfc: null,
    bCambioRfcReciente: false,
    dFechaUltimoMovimiento: null,
    vTipoUltimoMovimiento: ''
};

const pageSizeHistorial = 15;

const formatearFecha = (valor) => {
    if (!valor) return '—';
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return valor;
    const dia = fecha.getDate();
    const mes = fecha.toLocaleString('es-MX', { month: 'short' });
    const anio = fecha.getFullYear();
    const hora = String(fecha.getHours()).padStart(2, '0');
    const minutos = String(fecha.getMinutes()).padStart(2, '0');
    const mesCapitalizado = mes.charAt(0).toUpperCase() + mes.slice(1).replace('.', '');
    return `${dia} ${mesCapitalizado} ${anio}, ${hora}:${minutos}`;
};

const ModalHistorialPlanFacturacionMx = ({ show, onHide, idClienteAkasia, plan }) => {
    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [historialMovimientos, setHistorialMovimientos] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(false);
    const [cargandoMasHistorial, setCargandoMasHistorial] = useState(false);
    const [pageNumberHistorial, setPageNumberHistorial] = useState(1);
    const [hasMoreHistorial, setHasMoreHistorial] = useState(true);
    const [tipoMovimiento, setTipoMovimiento] = useState('');
    const [tiposMovimientoDisponibles, setTiposMovimientoDisponibles] = useState([]);
    const [resumenHistorial, setResumenHistorial] = useState(resumenHistorialDefault);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (!show) {
            setHistorialMovimientos([]);
            setPageNumberHistorial(1);
            setHasMoreHistorial(true);
            setTipoMovimiento('');
            setSearchText('');
            setTiposMovimientoDisponibles([]);
            setResumenHistorial(resumenHistorialDefault);
            return;
        }

        setPageNumberHistorial(1);
        setHasMoreHistorial(true);
    }, [show, plan?.idPlanFacturaMx]);

    useEffect(() => {
        if (!show || !plan?.idPlanFacturaMx || !idClienteAkasia) return;
        const append = pageNumberHistorial > 1;
        obtenerHistorialPlanFacturacionMx(pageNumberHistorial, tipoMovimiento, debouncedSearchText, append);
        // eslint-disable-next-line
    }, [show, plan?.idPlanFacturaMx, idClienteAkasia, pageNumberHistorial, tipoMovimiento, debouncedSearchText]);

    const obtenerHistorialPlanFacturacionMx = async (pageNumber = 1, movimientoFiltro = '', search = '', append = false) => {
        if (!plan?.idPlanFacturaMx) {
            setHistorialMovimientos([]);
            return;
        }

        if (append) {
            setCargandoMasHistorial(true);
        } else {
            setCargandoHistorial(true);
            setHistorialMovimientos([]);
            setResumenHistorial(resumenHistorialDefault);
        }

        try {
            const response = await fetch('/api/cliente/GetHistorialPlanFacturacionMx', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idClienteAkasia,
                    idPlanFactura: plan.idPlanFacturaMx,
                    pageSize: pageSizeHistorial,
                    pageNumber,
                    tipoMovimiento: movimientoFiltro || null,
                    searchText: search || null
                })
            });

            if (response.ok) {
                const data = await response.json();
                let nuevosMovimientos = [];
                if (data?.iResponseType === 1 && data?.detalles) {
                    const detalles = JSON.parse(data.detalles);
                    const resumen = detalles?.table2?.[0] || detalles?.Table2?.[0] || resumenHistorialDefault;
                    const tableMovimientos = detalles?.table3 || detalles?.Table3 || [];
                    const tableTipos = detalles?.table4 || detalles?.Table4 || [];
                    nuevosMovimientos = Array.isArray(tableMovimientos) ? tableMovimientos : [];
                    setResumenHistorial(resumen);
                    setTiposMovimientoDisponibles(Array.isArray(tableTipos)
                        ? tableTipos.map((t) => (t?.vTipoMovimiento || '').trim()).filter(Boolean)
                        : []);
                } else {
                    nuevosMovimientos = Array.isArray(data) ? data : data?.table1 || [];
                }

                setHistorialMovimientos((prev) => (append ? [...prev, ...nuevosMovimientos] : nuevosMovimientos));
                setHasMoreHistorial(nuevosMovimientos.length >= pageSizeHistorial);
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                showErrorToast(`Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            showErrorToast('Error al obtener historial de planes de facturación: ' + error.message);
        } finally {
            if (append) {
                setCargandoMasHistorial(false);
            } else {
                setCargandoHistorial(false);
            }
        }
    };

    const descargarHistorialExcel = () => {
        if (!historialMovimientos.length) {
            showErrorToast('No hay registros para exportar.');
            return;
        }

        const movimientosOrdenados = [...historialMovimientos].sort((a, b) => {
            const fechaA = new Date(a.dFechaMovimiento || a.dCreatedDate || 0).getTime();
            const fechaB = new Date(b.dFechaMovimiento || b.dCreatedDate || 0).getTime();
            return fechaB - fechaA;
        });

        const headers = [
            'Fecha', 'Usuario', 'TipoMovimiento', 'UUIDFactura', 'RFCAnterior',
            'RFCNuevo', 'SaldoAnterior', 'SaldoNuevo', 'Cambio', 'CantidadMovimiento'
        ];

        const escapeCsv = (value) => {
            const safeValue = value === null || value === undefined ? '' : String(value);
            return `"${safeValue.replaceAll('"', '""')}"`;
        };

        const rows = movimientosOrdenados.map((movimiento) => {
            const fechaMovimiento = formatearFecha(movimiento.dFechaMovimiento || movimiento.dCreatedDate);
            const saldoAnterior = Number(movimiento.iSaldoAnterior ?? 0);
            const saldoNuevo = Number(movimiento.iSaldoNuevo ?? 0);
            const cambio = Number.isNaN(saldoNuevo - saldoAnterior) ? 0 : saldoNuevo - saldoAnterior;
            return [
                fechaMovimiento,
                movimiento.vCreatedUser || '',
                movimiento.vTipoMovimiento || '',
                movimiento.vUUIDFactura || '',
                movimiento.vRfcAnterior || '',
                movimiento.vRfc || '',
                movimiento.iSaldoAnterior ?? '',
                movimiento.iSaldoNuevo ?? '',
                cambio,
                movimiento.iCantidadMovimiento ?? ''
            ].map(escapeCsv).join(',');
        });

        const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const rfc = (plan?.vRfc || 'plan').replace(/[^a-zA-Z0-9_-]/g, '');
        link.href = url;
        link.download = `historial_${rfc}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const handleScrollHistorial = (event) => {
        if (!show || cargandoHistorial || cargandoMasHistorial || !hasMoreHistorial || !plan) return;

        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight > 80) return;

        setPageNumberHistorial((prev) => prev + 1);
    };

    const handleFiltroTipoMovimiento = (event) => {
        setTipoMovimiento(event.target.value);
        setPageNumberHistorial(1);
        setHasMoreHistorial(true);
    };

    const handleFiltroSearchText = (value) => {
        setSearchText(value);
        setPageNumberHistorial(1);
        setHasMoreHistorial(true);
    };

    const renderHistorialMovimientos = () => {
        if (!plan) {
            return <p className="mb-0 text-muted">Selecciona un plan para ver su historial.</p>;
        }

        if (cargandoHistorial && historialMovimientos.length === 0) {
            return (
                <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando historial...
                </div>
            );
        }

        if (historialMovimientos.length === 0) {
            return <p className="mb-0 text-muted">No hay movimientos para este plan.</p>;
        }

        const movimientosOrdenados = [...historialMovimientos].sort((a, b) => {
            const fechaA = new Date(a.dFechaMovimiento || a.dCreatedDate || 0).getTime();
            const fechaB = new Date(b.dFechaMovimiento || b.dCreatedDate || 0).getTime();
            return fechaB - fechaA;
        });

        return (
            <div className="d-flex flex-column gap-1 px-3 pb-2">
                {movimientosOrdenados.map((movimiento) => {
                    const saldoAnterior = Number(movimiento.iSaldoAnterior ?? 0);
                    const saldoNuevo = Number(movimiento.iSaldoNuevo ?? 0);
                    const cambio = Number.isNaN(saldoNuevo - saldoAnterior) ? 0 : saldoNuevo - saldoAnterior;
                    const cambioBadge = cambio > 0 ? 'success' : cambio < 0 ? 'danger' : 'secondary';
                    const fechaMovimiento = formatearFecha(movimiento.dFechaMovimiento || movimiento.dCreatedDate);
                    const esEdicionRfc = (movimiento.vTipoMovimiento || '').trim().toLowerCase() === 'edicion de rfc';

                    return (
                        <Row
                            key={movimiento.idHistorialPlanFacturacionMX}
                            className="border rounded-3 px-2 py-1 shadow-sm"
                            style={{ background: '#f8fafc', borderColor: '#dbe3ee', margin: 0 }}
                        >
                            <div className="d-flex justify-content-between align-items-center gap-2 px-0">
                                <div className="text-uppercase text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.62rem', letterSpacing: '0.3px' }}>
                                    <FontAwesomeIcon icon={faUser} />
                                    <span>{movimiento.vCreatedUser || '—'}</span>
                                </div>
                                <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '0.68rem' }}>
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                    <span>{fechaMovimiento}</span>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center gap-2 px-0">
                                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                        {movimiento.vTipoMovimiento || '—'}
                                    </span>
                                    {movimiento.vUUIDFactura && (
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="text-muted" style={{ fontSize: '0.62rem', wordBreak: 'break-all' }}>
                                                UUID: <span className="text-dark fw-semibold">{movimiento.vUUIDFactura}</span>
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 m-0 ms-1 text-primary d-flex align-items-center justify-content-center"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(movimiento.vUUIDFactura);
                                                    showSuccessToast('UUID copiado al portapapeles');
                                                }}
                                                title="Copiar UUID"
                                                style={{ width: '20px', height: '20px' }}
                                            >
                                                <FontAwesomeIcon icon={faCopy} style={{ fontSize: '0.8rem' }} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center gap-2 px-0">
                                {esEdicionRfc ? (
                                    <div className="d-flex align-items-center gap-1 fw-semibold">
                                        <SoftBadge bg="danger" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            {movimiento.vRfcAnterior || '—'}
                                        </SoftBadge>
                                        <FontAwesomeIcon icon={faArrowRight} className="text-muted" style={{ fontSize: '0.7rem' }} />
                                        <SoftBadge bg="success" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            {movimiento.vRfc || '—'}
                                        </SoftBadge>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                        <div className="d-flex align-items-center gap-1 fw-semibold" style={{ fontSize: '0.8rem' }}>
                                            <span>{movimiento.iSaldoAnterior ?? '—'}</span>
                                            <FontAwesomeIcon icon={faArrowRight} className="text-muted" style={{ fontSize: '0.7rem' }} />
                                            <span>{movimiento.iSaldoNuevo ?? '—'}</span>
                                        </div>
                                        <SoftBadge bg={cambioBadge} style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                                            {cambio >= 0 ? '+' : ''}{cambio}
                                        </SoftBadge>
                                    </div>
                                )}
                            </div>
                        </Row>
                    );
                })}

                {cargandoMasHistorial && (
                    <div className="d-flex justify-content-center py-2 text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Cargando más movimientos...
                    </div>
                )}

                {!hasMoreHistorial && movimientosOrdenados.length > 0 && (
                    <div className="text-center text-muted small py-2">
                        No hay más movimientos por cargar.
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal show={show} centered onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Historial de Movimientos
                    {plan ? ` - ${plan.vRfc}` : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }} onScroll={handleScrollHistorial}>
                <Row className="d-flex justify-content-center align-items-center mb-3 px-3">
                    <Col xs={12} className="d-flex justify-content-end align-items-center gap-2 mb-2">
                        <SoftBadge bg="primary">Movimientos: {resumenHistorial.iTotalMovimientos ?? 0}</SoftBadge>
                        <SoftBadge bg="success">Timbres actuales: {plan?.iCantidades ?? 0}</SoftBadge>
                        {resumenHistorial.iEventosUsoTimbres > 0 && (
                            <SoftBadge bg="info">Timbres usados: {resumenHistorial.iEventosUsoTimbres ?? 0}</SoftBadge>
                        )}
                        {resumenHistorial.iTotalTimbresComprados > 0 && (
                            <SoftBadge bg="info">Timbres comprados: {resumenHistorial.iTotalTimbresComprados ?? 0}</SoftBadge>
                        )}
                        {resumenHistorial.iTotalCambiosRfc > 0 && (
                            <SoftBadge bg="info">Cambios RFC: {resumenHistorial.iTotalCambiosRfc ?? 0}</SoftBadge>
                        )}
                    </Col>
                    <Col xs={3}><></></Col>
                    <Col xs={3}>
                        <Form.Group>
                            <Form.Label className="mb-1">Filtro movimiento</Form.Label>
                            <Form.Select value={tipoMovimiento} onChange={handleFiltroTipoMovimiento}>
                                <option value="">Todos</option>
                                {tiposMovimientoDisponibles.map((tipo) => (
                                    <option key={tipo} value={tipo}>{tipo}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col xs={6}>
                        <Form.Group>
                            <Form.Label className="mb-1">Filtrar por UUID de la factura</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Buscar por UUID de la factura"
                                value={searchText}
                                onChange={(e) => handleFiltroSearchText(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                {renderHistorialMovimientos()}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-success" onClick={descargarHistorialExcel} disabled={!historialMovimientos.length}>
                    Descargar Excel
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalHistorialPlanFacturacionMx.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idClienteAkasia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    plan: PropTypes.shape({
        idPlanFacturaMx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        vRfc: PropTypes.string,
        iCantidades: PropTypes.number
    })
};

export default ModalHistorialPlanFacturacionMx;
